import React, { useEffect, useMemo, useState } from 'react';

import { DateTime } from 'luxon';

import { Modal, Alert } from '@shared/components/bootstrap';
import { LightboxWithCarousel, ILightboxCarouselInput, Picture } from '@shared/components/helpers';
import { useLegacyAttachmentUpload } from '@shared/hooks';
import { UserRole } from '@shared/types/user_role';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { SingleSelectFormControl } from '@admin/components/fields/single_select_form_control';
import { Roles } from '@admin/components/helpers/roles';
import { titleize } from '@admin/components/helpers/titleize';
import { Table as HistoriesTable } from '@admin/components/histories/table';
import { Barcode as ItemBarcode } from '@admin/components/items/barcode';
import { DuplicateItemLinking } from '@admin/components/items/duplicate_item_linking';
import { Spinner } from '@admin/components/spinner';
import { ROLES } from '@admin/config/roles';
import { client } from '@admin/libraries/apollo';
import {
  History__Subject,
  IncidentDamageLocation,
  IncidentDamageType,
  IncidentFragment,
  IncidentImageFragment,
  IncidentKindEnum,
  IncidentProhibitedCategoryEnum,
  IncidentStateEnum,
  IncidentUnsafeReviewStatus,
  Incident__Create__Input,
  Incident__Update__Input,
  ImageState,
  Status,
  useIncidentsForItemQuery,
  useIncidentsForPalletQuery,
  useIncidentCreateMutation,
  useIncidentProhibitedItemCategoryOptionsQuery,
  useIncidentStructuredDamageOptionsQuery,
  useIncidentResolveMutation,
  useIncidentUpdateMutation,
  IncidentsForItemDocument,
  IncidentsForPalletDocument,
  ItemFragment,
} from '@admin/schema';
import { INCIDENT_KIND_DISPLAY_LABELS } from '@admin/utils/incident_kind_labels';

type ParentType = 'items' | 'pallets';

interface IncidentsPanelProps {
  kind: 'item' | 'pallet';
  parentType: ParentType;
  parentID: string;
  accountID?: string;
  timezone?: string;
}

interface IncidentKindOption {
  value: string;
  label: string;
}

const INCIDENT_KIND_OPTIONS: IncidentKindOption[] = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'ped', label: 'Damaged Before Pickup' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'missing', label: 'Missing' },
  { value: 'prohibited', label: 'Prohibited' },
  { value: 'wh_created', label: 'Warehouse Created' },
];

/** Mirrors Incident#flaggable? — only these kinds move the resource to flagged on create. */
const incidentKindFlagsResource = (kindLower: string): boolean =>
  kindLower === 'missing' || kindLower === 'duplicate' || kindLower === 'auction';

const getStateLabelClass = (state: string): string => {
  switch (state.toLowerCase()) {
    case 'open':
      return 'label-warning';
    case 'resolved':
      return 'label-success';
    default:
      return 'label-default';
  }
};

const formatDateTime = (dateTime: string, timezone?: string): string => {
  const dt = DateTime.fromISO(dateTime);
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return dt.setZone(tz).toFormat('MMM d, y, h:mm:ss a');
};

/** GraphQL enums arrive as SCREAMING_SNAKE; DB may store snake_case — normalize for display. */
function normalizeEnumLabel(value: string): string {
  const customLabels: Record<string, string> = {
    ...INCIDENT_KIND_DISPLAY_LABELS,
  };
  return customLabels[value.toLowerCase()] || titleize(value.toLowerCase());
}

/** GraphQL enums arrive as SCREAMING_SNAKE; DB may store snake_case — normalize for display. */
const formatDamageEnumList = (values: readonly string[] | null | undefined): string | null => {
  if (!values || values.length === 0) return null;
  return values.map((v) => normalizeEnumLabel(v)).join(', ');
};

/** Multi-select for GraphQL incident enum fields (damage, prohibited categories, etc.). */
function IncidentEnumCheckboxes<T extends string>({
  label,
  options,
  selected,
  onChange,
  disabled,
}: {
  label: string;
  options: readonly T[];
  selected: readonly T[];
  onChange: (next: T[]) => void;
  disabled?: boolean;
}) {
  const selectedSet = new Set(selected);
  const toggle = (value: T) => {
    const next = new Set(selectedSet);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onChange(Array.from(next));
  };

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="well well-sm" style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 0 }}>
        {options.map((opt) => (
          <div key={opt} className="checkbox" style={{ marginTop: 0, marginBottom: 4 }}>
            <label style={{ fontWeight: 'normal' }}>
              <input type="checkbox" checked={selectedSet.has(opt)} disabled={disabled} onChange={() => toggle(opt)} />{' '}
              {normalizeEnumLabel(opt)}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

const IncidentProhibitedSummary: React.FC<{ incident: IncidentFragment }> = ({ incident }) => {
  const cats = incident.prohibitedCategories;
  if (!cats || cats.length === 0) return null;
  const text = cats.map((v) => normalizeEnumLabel(v)).join(', ');
  return (
    <div className="text-muted small" style={{ marginTop: '0.35rem' }}>
      <div>
        <strong>Prohibited categories:</strong> {text}
      </div>
    </div>
  );
};

const IncidentStructuredDamageSummary: React.FC<{ incident: IncidentFragment }> = ({ incident }) => {
  const types = incident.damageTypes;
  const locations = incident.damageLocations;
  const hasAny = (types && types.length > 0) || (locations && locations.length > 0);
  if (!hasAny) return null;

  const line = (label: string, values: readonly string[] | null | undefined) => {
    const text = formatDamageEnumList(values);
    if (!text) return null;
    return (
      <div>
        <strong>{label}:</strong> {text}
      </div>
    );
  };

  return (
    <div className="text-muted small" style={{ marginTop: '0.35rem' }}>
      {line('Types', types)}
      {line('Locations', locations)}
    </div>
  );
};

const IncidentMissingMeta: React.FC<{ incident: IncidentFragment }> = ({ incident }) => {
  if (incident.kind.toLowerCase() !== 'missing') return null;

  if (!incident.fieldResponseForMissingInbound) return null;

  return (
    <div className="text-muted small" style={{ marginTop: '0.35rem' }}>
      <div>
        <strong>Field Response:</strong> {normalizeEnumLabel(incident.fieldResponseForMissingInbound)}
      </div>
    </div>
  );
};

const canManageUnsafeHandling = (): boolean => ROLES.has(UserRole.Admin) || ROLES.has(UserRole.Manager);

const isUnsafeFlagged = (incident: IncidentFragment): boolean =>
  incident.kind === IncidentKindEnum.Damaged &&
  (incident.unsafeReviewStatus === IncidentUnsafeReviewStatus.Unreviewed ||
    incident.unsafeReviewStatus === IncidentUnsafeReviewStatus.Confirmed);

const incidentKindDisplayLabel = (incident: IncidentFragment): string => {
  const option = INCIDENT_KIND_OPTIONS.find((o) => o.value === incident.kind.toLowerCase());
  return option?.label || normalizeEnumLabel(incident.kind.toLowerCase());
};

const unsafeHandlingDisplayLabel = (incident: IncidentFragment): string | null => {
  if (!isUnsafeFlagged(incident)) {
    return null;
  }
  if (incident.unsafeReviewStatus === IncidentUnsafeReviewStatus.Unreviewed) {
    return 'Unsafe to handle (Unreviewed)';
  }
  return 'Unsafe to handle';
};

const IncidentUnsafeHandlingSummary: React.FC<{ incident: IncidentFragment }> = ({ incident }) => {
  const unsafeLabel = unsafeHandlingDisplayLabel(incident);
  const notes = incident.unsafeReviewNotes?.trim();
  if (!unsafeLabel && !notes) return null;

  return (
    <div className="text-muted small" style={{ marginTop: '0.35rem' }}>
      {unsafeLabel && (
        <div>
          <strong>{unsafeLabel}</strong>
        </div>
      )}
      {notes && (
        <div>
          <strong>Unsafe review notes:</strong> {notes}
        </div>
      )}
    </div>
  );
};

interface ViewLocationProps {
  location: {
    id: string;
    value: string;
    warehouse?: {
      id: string;
      name: string;
    } | null;
  };
  showWarehouse?: boolean;
}

const ViewLocation: React.FC<ViewLocationProps> = ({ location, showWarehouse }) => (
  <a href={`/admin/locations/${location.id}`} className="text-primary">
    #{location.value}
    {showWarehouse && location.warehouse && <span> ({location.warehouse.name})</span>}
  </a>
);

interface StateLabelProps {
  state: string;
}

const StateLabel: React.FC<StateLabelProps> = ({ state }) => (
  <span className={`label ${getStateLabelClass(state)}`}>
    <span>{titleize(state)}</span>
  </span>
);

interface UploadItem {
  id: string;
  file: File;
  url?: string;
}

interface FileUploadProps {
  file: File;
  onUpload: (url: string) => void;
  onError: (error: string) => void;
  onRemove: () => void;
}

const FileUploadItem: React.FC<FileUploadProps> = ({ file, onUpload, onError, onRemove }) => {
  const [preview, setPreview] = useState<string>('');
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, [file]);

  const { loaded, total } = useLegacyAttachmentUpload({
    file,
    onUpload: (url) => {
      setUploadedUrl(url);
      onUpload(url);
    },
    onError: (error) => {
      onError(typeof error === 'string' ? error : error.message);
    },
  });

  const progress = total ? (loaded || 0) / total : 0;
  const uploadState = uploadedUrl ? 'success' : progress > 0 ? 'uploading' : 'pending';

  const progressBarClass = {
    uploading: 'progress-bar-info',
    success: 'progress-bar-success',
    failure: 'progress-bar-warning',
    pending: 'progress-bar-info',
  }[uploadState];

  return (
    <tr>
      <td className="col-sm-2">
        <span className="thumbnail">
          <img width="240" height="240" src={preview} alt={file.name} />
        </span>
      </td>
      <td className="col-sm-8 text-left">
        {file.name}
        {progress > 0 && (
          <div className="progress">
            <div
              className={`progress-bar ${progressBarClass}`}
              role="progressbar"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        )}
        <div className="text-center">
          {uploadState === 'uploading' && <span>{(progress * 100).toFixed(2)}%</span>}
          {uploadState === 'success' && <span>Uploaded</span>}
        </div>
      </td>
      <td className="col-sm-2 text-right">
        <button className="btn btn-danger" onClick={onRemove}>
          Remove
        </button>
      </td>
    </tr>
  );
};

interface IncidentDialogProps {
  incident: Partial<IncidentFragment> & { parent_type: ParentType; parent_id: string };
  resource: {
    id: string;
    state: string;
    accountID?: string;
    duplicateOfID?: string | null;
  };
  onSave: () => void;
  onCancel: () => void;
}

const IncidentDialog: React.FC<IncidentDialogProps> = ({ incident, resource, onSave, onCancel }) => {
  const isEditing = !!incident.id;
  const originalKindNormalized = useMemo(() => (incident.kind ?? '').toString().toLowerCase(), [incident.kind]);
  const [kind, setKind] = useState<string>(() => originalKindNormalized);
  const [explanation, setExplanation] = useState<string>(incident.explanation || '');
  const [confirmState, setConfirmState] = useState<boolean>(resource.state.toLowerCase() === 'flagged');
  const [duplicateOf, setDuplicateOf] = useState<ItemFragment | undefined>(undefined);
  const [duplicateLinkChanged, setDuplicateLinkChanged] = useState<boolean>(false);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [damageTypes, setDamageTypes] = useState<IncidentDamageType[]>(() => incident.damageTypes ?? []);
  const [damageLocations, setDamageLocations] = useState<IncidentDamageLocation[]>(
    () => incident.damageLocations ?? [],
  );
  const [prohibitedCategories, setProhibitedCategories] = useState<IncidentProhibitedCategoryEnum[]>(() =>
    incident.prohibitedCategories?.length ? (incident.prohibitedCategories as IncidentProhibitedCategoryEnum[]) : [],
  );
  const [unsafeToHandle, setUnsafeToHandle] = useState<boolean>(() =>
    incident.id ? isUnsafeFlagged(incident as IncidentFragment) : false,
  );

  const kindLower = kind.toLowerCase();
  const editingUnsafeFlagged = isEditing && isUnsafeFlagged(incident as IncidentFragment);
  const isUnsafeToggleLocked = editingUnsafeFlagged && !canManageUnsafeHandling();
  const showStructuredDamageFields = kindLower === 'damaged' || kindLower === 'ped';
  const showProhibitedFields = kindLower === 'prohibited';

  const {
    data: damageOptionsData,
    loading: damageOptionsLoading,
    error: damageOptionsError,
  } = useIncidentStructuredDamageOptionsQuery({
    client,
    skip: !showStructuredDamageFields,
  });

  const {
    data: prohibitedOptionsData,
    loading: prohibitedOptionsLoading,
    error: prohibitedOptionsError,
  } = useIncidentProhibitedItemCategoryOptionsQuery({
    client,
    skip: !showProhibitedFields,
  });

  // Catalog only — do not union selected keys so retired/legacy values cannot be re-saved via edit UI.
  const typeOptions = useMemo(
    () => damageOptionsData?.incidentStructuredDamageOptions.damageTypes ?? [],
    [damageOptionsData],
  );
  const locationOptions = useMemo(
    () => damageOptionsData?.incidentStructuredDamageOptions.damageLocations ?? [],
    [damageOptionsData],
  );
  const prohibitedCategoryOptions = useMemo(
    () => prohibitedOptionsData?.incidentProhibitedItemCategoryOptions.itemCategories ?? [],
    [prohibitedOptionsData],
  );

  // Drop selected keys that are no longer in the catalog (post-remap / retired values).
  useEffect(() => {
    if (typeOptions.length > 0) {
      const allowed = new Set(typeOptions);
      setDamageTypes((prev) => prev.filter((t) => allowed.has(t)));
    }
    if (locationOptions.length > 0) {
      const allowed = new Set(locationOptions);
      setDamageLocations((prev) => prev.filter((l) => allowed.has(l)));
    }
    if (prohibitedCategoryOptions.length > 0) {
      const allowed = new Set(prohibitedCategoryOptions);
      setProhibitedCategories((prev) => prev.filter((c) => allowed.has(c)));
    }
  }, [typeOptions, locationOptions, prohibitedCategoryOptions]);

  const handleDuplicateLinkChange = (item: ItemFragment | undefined) => {
    setDuplicateOf(item);
    setDuplicateLinkChanged(true);
  };

  const handleUnsafeToHandleChange = (checked: boolean) => {
    if (isUnsafeToggleLocked) {
      return;
    }
    setUnsafeToHandle(checked);
  };

  const incidentKindOptions = (() => {
    if (isEditing && incident.kind) {
      const kindValue = incident.kind.toLowerCase();
      return INCIDENT_KIND_OPTIONS.filter((o) => o.value === kindValue);
    }
    return INCIDENT_KIND_OPTIONS;
  })();

  const [createIncident, { loading: createLoading }] = useIncidentCreateMutation({ client });
  const [updateIncident, { loading: updateLoading }] = useIncidentUpdateMutation({ client });

  const saving = createLoading || updateLoading;

  const getRefetchQueries = () => [
    incident.parent_type === 'items'
      ? { query: IncidentsForItemDocument, variables: { accountID: resource.accountID!, itemID: incident.parent_id } }
      : { query: IncidentsForPalletDocument, variables: { palletID: incident.parent_id } },
  ];

  const prohibitedSelectionIncludesOther = prohibitedCategories.includes(IncidentProhibitedCategoryEnum.Other);

  const requiresItemFlagConfirmation =
    !isEditing &&
    resource.state.toLowerCase() !== 'flagged' &&
    kindLower !== 'wh_created' &&
    incidentKindFlagsResource(kindLower);

  const flagConfirmationOk = !requiresItemFlagConfirmation || confirmState;

  const savable = (): boolean => {
    if (!kind) return false;

    const hasExplanation = !!(explanation && explanation.trim().length > 0);
    const trimmedExplanation = explanation.trim();

    // For NEW duplicate incidents, require a parent item
    if (!isEditing && kind.toUpperCase() === IncidentKindEnum.Duplicate && !duplicateOf?.id) {
      return false;
    }

    // When EDITING and CHANGING kind TO duplicate, require parent
    if (
      isEditing &&
      originalKindNormalized !== kind &&
      kind.toUpperCase() === IncidentKindEnum.Duplicate &&
      !duplicateOf?.id
    ) {
      return false;
    }

    if (!isEditing) {
      // WH_CREATED incidents don't require flagging confirmation
      if (kindLower === 'wh_created') {
        return hasExplanation;
      }
      if (kindLower === 'prohibited') {
        const hasCats = prohibitedCategories.length > 0;
        const otherOk = !prohibitedSelectionIncludesOther || trimmedExplanation.length > 0;
        return hasCats && otherOk && flagConfirmationOk;
      }
      if (kindLower === 'damaged') {
        // Causes not collected in Admin/Dock. Types + locations required when any structured field is set.
        const damagedPartialStructured = damageTypes.length > 0 || damageLocations.length > 0;
        if (damagedPartialStructured) {
          return damageTypes.length > 0 && damageLocations.length > 0 && flagConfirmationOk;
        }
        return hasExplanation && flagConfirmationOk;
      }
      if (kindLower === 'ped') {
        const pedPartialStructured = damageTypes.length > 0 || damageLocations.length > 0;
        if (pedPartialStructured) {
          return damageTypes.length > 0 && damageLocations.length > 0 && flagConfirmationOk;
        }
        return hasExplanation && flagConfirmationOk;
      }
      return hasExplanation && flagConfirmationOk;
    }

    if (kindLower === 'prohibited') {
      const hasCats = prohibitedCategories.length > 0;
      const otherOk = !prohibitedSelectionIncludesOther || trimmedExplanation.length > 0;
      return hasCats && otherOk;
    }

    // Mirror Incident model: damaged needs types + locations if any structured field is set.
    if (kindLower === 'damaged') {
      const damagedPartialStructured = damageTypes.length > 0 || damageLocations.length > 0;
      if (damagedPartialStructured) {
        return damageTypes.length > 0 && damageLocations.length > 0;
      }
      return hasExplanation;
    }
    // PED: types + locations (no causes).
    if (kindLower === 'ped') {
      const pedPartialStructured = damageTypes.length > 0 || damageLocations.length > 0;
      if (pedPartialStructured) {
        return damageTypes.length > 0 && damageLocations.length > 0;
      }
      return hasExplanation;
    }
    return hasExplanation;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newUploads = Array.from(files).map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        file,
      }));
      setUploads((prev) => [...prev, ...newUploads]);
    }
    event.target.value = '';
  };

  const handleFileUpload = (id: string, url: string) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, url } : u)));
  };

  const handleFileRemove = (id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  const handleSave = async () => {
    if (!savable()) {
      // Check if it's a duplicate validation error
      const isCreatingDuplicate = !isEditing && kind.toUpperCase() === IncidentKindEnum.Duplicate && !duplicateOf?.id;
      const isChangingToDuplicate =
        isEditing &&
        originalKindNormalized !== kind &&
        kind.toUpperCase() === IncidentKindEnum.Duplicate &&
        !duplicateOf?.id;

      if (isCreatingDuplicate || isChangingToDuplicate) {
        setError('Please select a parent item for this duplicate incident.');
      } else {
        setError('Please review the form prior to saving.');
      }
      return;
    }

    if (!isEditing && uploads.length === 0 && (kindLower === 'damaged' || kindLower === 'prohibited')) {
      setError('Please upload at least one image prior to saving.');
      return;
    }

    // Check all uploads are complete
    const pendingUploads = uploads.filter((u) => !u.url);
    if (pendingUploads.length > 0) {
      setError('Please wait for all uploads to complete.');
      return;
    }

    setError(null);

    try {
      if (isEditing) {
        // Only send duplicateOfID if the user has explicitly changed the link
        // Send null to clear, id to set, undefined to leave unchanged
        let duplicateOfIDValue: string | null | undefined = undefined;
        if (kind.toUpperCase() === IncidentKindEnum.Duplicate && duplicateLinkChanged) {
          duplicateOfIDValue = duplicateOf?.id ?? null;
        }

        const updateInput: Incident__Update__Input = {
          id: incident.id!,
          parentType: incident.parent_type,
          parentID: incident.parent_id,
          kind: kind.toUpperCase() as IncidentKindEnum,
          explanation,
          duplicateOfID: duplicateOfIDValue,
          requireParentForDuplicate: kind.toUpperCase() === IncidentKindEnum.Duplicate,
        };

        if (kindLower === 'damaged' || kindLower === 'ped') {
          updateInput.damageTypes = damageTypes;
          updateInput.damageLocations = damageLocations;
          // Admin/Dock never collect causes; omit so existing Field values stay until the column is dropped.
          if (kindLower === 'ped') {
            updateInput.damageCauses = [];
          }
        }
        if (kindLower === 'prohibited') {
          updateInput.prohibitedCategories = prohibitedCategories;
        }
        if (kindLower === 'damaged' && !isUnsafeToggleLocked) {
          updateInput.unsafeToHandle = unsafeToHandle;
        }

        const result = await updateIncident({
          variables: {
            input: updateInput,
          },
          refetchQueries: getRefetchQueries(),
        });

        if (result.data?.incidentUpdate?.status === Status.Ok) {
          onSave();
        } else {
          setError(result.data?.incidentUpdate?.error || 'Failed to update incident');
        }
      } else {
        const imagesAttributes = uploads.map((u) => ({ url: u.url!, state: ImageState.Uploaded }));

        const createInput: Incident__Create__Input = {
          parentType: incident.parent_type,
          parentID: incident.parent_id,
          kind: kind.toUpperCase() as IncidentKindEnum,
          explanation: explanation.trim().length > 0 ? explanation : undefined,
          duplicateOfID: kind.toUpperCase() === IncidentKindEnum.Duplicate ? duplicateOf?.id : undefined,
          requireParentForDuplicate: kind.toUpperCase() === IncidentKindEnum.Duplicate,
          images: imagesAttributes.length > 0 ? imagesAttributes : undefined,
        };

        if (kindLower === 'damaged' || kindLower === 'ped') {
          createInput.damageTypes = damageTypes;
          createInput.damageLocations = damageLocations;
        }

        if (kindLower === 'prohibited' && prohibitedCategories.length > 0) {
          createInput.prohibitedCategories = prohibitedCategories;
        }
        if (kindLower === 'damaged' && unsafeToHandle) {
          createInput.unsafeToHandle = true;
        }

        const result = await createIncident({
          variables: {
            input: createInput,
          },
          refetchQueries: getRefetchQueries(),
        });

        if (result.data?.incidentCreate?.status === Status.Ok) {
          onSave();
        } else {
          setError(result.data?.incidentCreate?.error || 'Failed to create incident');
        }
      }
    } catch (e) {
      setError((e as Error).message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <Modal onClose={onCancel} backdrop="static">
      <Modal.Content>
        <div className="dialog">
          <p className="dialog-title">{isEditing ? 'Edit' : 'Report'} Incident</p>
          <div className="dialog-contents">
            {error && <Alert style="danger">{error}</Alert>}

            <div className="form-group">
              <label htmlFor="incident-kind">Incident Type:</label>
              <SingleSelectFormControl
                id="incident-kind"
                options={incidentKindOptions as Array<{ value: string; label: string }>}
                value={kind || undefined}
                onChange={(value) => {
                  const next = value || '';
                  const nextLower = next.toLowerCase();
                  setKind(next);
                  if (nextLower !== 'prohibited') {
                    setProhibitedCategories([]);
                  }
                  if (nextLower !== 'damaged' && nextLower !== 'ped') {
                    setDamageTypes([]);
                    setDamageLocations([]);
                  }
                  if (nextLower !== 'damaged') {
                    setUnsafeToHandle(false);
                  }
                }}
                placeholder="- Kind -"
                disabled={isEditing}
              />
            </div>

            {kind.toLowerCase() === 'duplicate' && incident.parent_type === 'items' && resource.accountID && (
              <DuplicateItemLinking
                accountID={resource.accountID}
                itemID={resource.id}
                originalItemID={resource.duplicateOfID || undefined}
                onChange={handleDuplicateLinkChange}
              />
            )}

            <div className="form-group">
              <label htmlFor="incident-explanation">Description of Incident:</label>
              <textarea
                id="incident-explanation"
                name="incident[explanation]"
                className="form-control"
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
              />
              {kindLower === 'damaged' && (
                <p className="help-block small text-muted" style={{ marginTop: 6 }}>
                  Optional when damage types and locations are selected.
                </p>
              )}
              {kindLower === 'prohibited' && (
                <p className="help-block small text-muted" style={{ marginTop: 6 }}>
                  Required when &quot;Other&quot; is selected as a prohibited category. Otherwise optional additional
                  notes.
                </p>
              )}
            </div>

            {showStructuredDamageFields && (
              <div className="form-group">
                <label>Structured damage</label>
                {damageOptionsLoading && <Spinner />}
                {damageOptionsError && (
                  <Alert style="danger">
                    Could not load structured damage options. Refresh the page or try again later.
                  </Alert>
                )}
                {!damageOptionsLoading && !damageOptionsError && (
                  <>
                    <IncidentEnumCheckboxes<IncidentDamageType>
                      label="Damage types"
                      options={typeOptions}
                      selected={damageTypes}
                      onChange={setDamageTypes}
                    />
                    <IncidentEnumCheckboxes<IncidentDamageLocation>
                      label="Damage locations"
                      options={locationOptions}
                      selected={damageLocations}
                      onChange={setDamageLocations}
                    />
                  </>
                )}
              </div>
            )}

            {kindLower === 'damaged' && (
              <div className="form-group">
                <div className="checkbox" style={{ marginTop: 0 }}>
                  <label style={{ fontWeight: 'normal' }}>
                    <input
                      type="checkbox"
                      checked={unsafeToHandle}
                      disabled={isUnsafeToggleLocked}
                      onChange={(e) => handleUnsafeToHandleChange(e.target.checked)}
                    />{' '}
                    Unsafe to handle
                  </label>
                </div>
                {isUnsafeToggleLocked && (
                  <p className="help-block small text-muted" style={{ marginTop: 6 }}>
                    Unsafe handling is locked after reporting. A manager or admin can change this when editing.
                  </p>
                )}
              </div>
            )}

            {showProhibitedFields && (
              <div className="form-group">
                <label>Prohibited categories</label>
                {prohibitedOptionsLoading && <Spinner />}
                {prohibitedOptionsError && (
                  <Alert style="danger">
                    Could not load prohibited category options. Refresh the page or try again later.
                  </Alert>
                )}
                {!prohibitedOptionsLoading && !prohibitedOptionsError && (
                  <IncidentEnumCheckboxes<IncidentProhibitedCategoryEnum>
                    label="Why is this item prohibited?"
                    options={prohibitedCategoryOptions}
                    selected={prohibitedCategories}
                    onChange={setProhibitedCategories}
                  />
                )}
              </div>
            )}

            {!isEditing && (
              <>
                <div className="form-group">
                  <label>Attach photos (damage or prohibited):</label>
                  <table className="table">
                    <tbody>
                      {uploads.map((upload) => (
                        <FileUploadItem
                          key={upload.id}
                          file={upload.file}
                          onUpload={(url) => handleFileUpload(upload.id, url)}
                          onError={(err) => setError(err)}
                          onRemove={() => handleFileRemove(upload.id)}
                        />
                      ))}
                    </tbody>
                  </table>

                  <div className="teal-file-input">
                    <label className="btn btn-primary" htmlFor="file">
                      Add Files
                    </label>
                    <input
                      id="file"
                      name="incident[files]"
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                  </div>
                </div>

                <div className="form-group">
                  {requiresItemFlagConfirmation && (
                    <label>
                      <input
                        name="incident[confirmations]"
                        type="checkbox"
                        checked={confirmState}
                        onChange={(e) => setConfirmState(e.target.checked)}
                      />{' '}
                      I understand this will update from <strong>{resource.state}</strong> to <strong>flagged</strong>.
                    </label>
                  )}
                </div>
              </>
            )}
            <div className="dialog-actions">
              <button className="btn btn-default btn-lg" onClick={onCancel} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving || !savable()}>
                Save
              </button>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};

interface HistoryModalProps {
  incidentID: string;
  timezone?: string;
  onClose: () => void;
}

const HistoryModal: React.FC<HistoryModalProps> = ({ incidentID, timezone, onClose }) => (
  <Modal onClose={onClose}>
    <Modal.Content>
      <Modal.Header>
        <Modal.Close close={onClose} />
        <Modal.Title>History</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <HistoriesTable id={incidentID} type={History__Subject.Incident} timezone={timezone} />
      </Modal.Body>
    </Modal.Content>
  </Modal>
);

function canResolveIncidentWithResolutionFlow(incident: IncidentFragment): boolean {
  if (incident.state !== IncidentStateEnum.Open) return false;
  return (
    incident.kind === IncidentKindEnum.Damaged ||
    incident.kind === IncidentKindEnum.Ped ||
    incident.kind === IncidentKindEnum.Prohibited
  );
}

interface ResolveIncidentModalProps {
  incident: IncidentFragment;
  parentType: ParentType;
  parentID: string;
  accountID?: string;
  isItem: boolean;
  onClose(): void;
}

const ResolveIncidentModal: React.FC<ResolveIncidentModalProps> = ({
  incident,
  parentType,
  parentID,
  accountID,
  isItem,
  onClose,
}) => {
  const [resolution, setResolution] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [resolveMutation, { loading: resolving }] = useIncidentResolveMutation({ client });

  const refetchQueries = isItem
    ? [{ query: IncidentsForItemDocument, variables: { accountID: accountID!, itemID: parentID } }]
    : [{ query: IncidentsForPalletDocument, variables: { palletID: parentID } }];

  const submit = async () => {
    const trimmed = resolution.trim();
    if (!trimmed) {
      setFormError('Resolution explanation is required.');
      return;
    }
    setFormError(null);
    const result = await resolveMutation({
      variables: {
        input: {
          id: incident.id,
          parentType,
          parentID,
          resolution: trimmed,
        },
      },
      refetchQueries,
    });
    if (result.data?.incidentResolve?.status === Status.Ok) {
      onClose();
    } else {
      setFormError(result.data?.incidentResolve?.error ?? 'Could not resolve incident.');
    }
  };

  return (
    <Modal onClose={onClose} backdrop="static">
      <Modal.Content>
        <div className="dialog">
          <p className="dialog-title">Resolve incident</p>
          <div className="dialog-contents">
            {formError && <Alert style="danger">{formError}</Alert>}
            <div className="form-group">
              <label htmlFor="incident-resolution">Resolution explanation</label>
              <textarea
                id="incident-resolution"
                className="form-control"
                rows={4}
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
              />
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn btn-default btn-lg" onClick={onClose} disabled={resolving}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={() => void submit()}
                disabled={resolving}
              >
                {resolving ? 'Saving…' : 'Resolve'}
              </button>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export const IncidentsPanel: React.FC<IncidentsPanelProps> = ({ kind, parentType, parentID, accountID, timezone }) => {
  const [dialogIncident, setDialogIncident] = useState<
    (Partial<IncidentFragment> & { parent_type: ParentType; parent_id: string }) | null
  >(null);
  const [historyIncidentID, setHistoryIncidentID] = useState<string | null>(null);
  const [historyTimezone, setHistoryTimezone] = useState<string | undefined>(undefined);
  const [incidentToResolve, setIncidentToResolve] = useState<IncidentFragment | null>(null);
  const [lightboxImages, setLightboxImages] = useState<ILightboxCarouselInput[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);

  const isItem = parentType === 'items';
  const readOnly = kind === 'pallet';

  const itemQuery = useIncidentsForItemQuery({
    client,
    variables: { accountID: accountID!, itemID: parentID },
    skip: !isItem || !accountID,
  });

  const palletQuery = useIncidentsForPalletQuery({
    client,
    variables: { palletID: parentID },
    skip: isItem,
  });

  const loading = isItem ? itemQuery.loading : palletQuery.loading;
  const error = isItem ? itemQuery.error : palletQuery.error;
  const resource = isItem ? itemQuery.data?.item : palletQuery.data?.pallet;
  const incidents = resource?.incidents || [];

  const handleNew = () => {
    if (!resource) return;
    setDialogIncident({
      parent_type: parentType,
      parent_id: parentID,
    });
  };

  const handleEdit = (incident: IncidentFragment) => {
    setDialogIncident({
      ...incident,
      parent_type: parentType,
      parent_id: parentID,
    });
  };

  const handleHistory = (incident: IncidentFragment) => {
    setHistoryIncidentID(incident.id);
    setHistoryTimezone(incident.user?.tz || timezone);
  };

  const handleDialogClose = () => {
    setDialogIncident(null);
  };

  const handleDialogSave = () => {
    setDialogIncident(null);
  };

  const handleImageClick = (images: IncidentImageFragment[], index: number) => {
    const lightboxInputs = images.map((img) => ({ src: img.source }));
    setLightboxImages(lightboxInputs);
    setLightboxIndex(index);
  };

  return (
    <div className="incidents panel">
      <div className="panel-heading">
        <h3 className="panel-title">Incidents</h3>
      </div>
      <div className="panel-body">
        {loading && <Spinner />}

        {error && <Alert style="danger">Unable to load incidents. Please try again.</Alert>}

        {!loading && !error && isItem && !accountID && (
          <Alert style="warning">Account ID is required to load incidents.</Alert>
        )}

        {!loading && !error && (!isItem || accountID) && incidents.length > 0 && (
          <table className="table table-striped">
            <thead>
              <tr>
                <th className="col-sm-2 text-left">Date</th>
                <th className="col-sm-2 text-left">Reporter</th>
                <th className="col-sm-3 text-left">Explanation</th>
                <th className="col-sm-3 text-left">Images</th>
                <th className="col-sm-1 text-left">Last Known Location</th>
                {isItem && <th className="col-sm-1 text-left">Duplicate Of</th>}
                <th className="col-zero text-left">Status</th>
                <th className="col-zero text-left"></th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((incident) => (
                <tr key={incident.id}>
                  <td className="col-sm-2 text-left">{formatDateTime(incident.timestamp, timezone)}</td>
                  <td className="col-sm-2 text-left">{incident.user?.name}</td>
                  <td className="col-sm-3 text-left">
                    <div>
                      <strong>{incidentKindDisplayLabel(incident)}</strong> <span>{incident.explanation}</span>
                    </div>
                    {incident.state === IncidentStateEnum.Resolved && incident.resolution ? (
                      <div className="text-muted small" style={{ marginTop: '0.35rem' }}>
                        <strong>Resolution:</strong> {incident.resolution}
                      </div>
                    ) : null}
                    <IncidentStructuredDamageSummary incident={incident} />
                    <IncidentMissingMeta incident={incident} />
                    <IncidentProhibitedSummary incident={incident} />
                    <IncidentUnsafeHandlingSummary incident={incident} />
                  </td>
                  <td className="col-sm-3 text-left">
                    <div className="row">
                      {incident.images.map((image, index) => (
                        <div key={image.id} className="col-lg-2 col-md-4 col-sm-6">
                          <button
                            type="button"
                            className="thumbnail"
                            aria-label={`View incident image ${index + 1}`}
                            onClick={() => handleImageClick(incident.images, index)}
                          >
                            <Picture
                              source={image.state === ImageState.Uploaded ? image.source : undefined}
                              width={320}
                              height={320}
                              background={ImgixBG.White}
                              fit={ImgixFit.Fill}
                              className="img-responsive"
                              alt={`Incident image ${index + 1}`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="col-sm-1 text-left">
                    {incident.lastKnownLocation && <ViewLocation location={incident.lastKnownLocation} showWarehouse />}
                  </td>
                  {isItem && (
                    <td className="col-sm-1 text-left">
                      {incident.kind.toLowerCase() === 'duplicate' &&
                        resource &&
                        'duplicateOfID' in resource &&
                        'accountID' in resource &&
                        resource.duplicateOfID && (
                          <a
                            href={`/accounts/${resource.accountID}/items/${resource.duplicateOfID}`}
                            className="text-primary"
                          >
                            <ItemBarcode accountID={resource.accountID} id={resource.duplicateOfID} />
                          </a>
                        )}
                    </td>
                  )}
                  <td className="col-zero col-nowrap">
                    <StateLabel state={incident.state} />
                  </td>
                  <td className="col-zero col-nowrap">
                    {!readOnly && incident.state === IncidentStateEnum.Open && (
                      <button type="button" className="btn btn-default" onClick={() => handleEdit(incident)}>
                        Edit
                      </button>
                    )}
                    {!readOnly && canResolveIncidentWithResolutionFlow(incident) && (
                      <button type="button" className="btn btn-default" onClick={() => setIncidentToResolve(incident)}>
                        Resolve
                      </button>
                    )}
                    <button className="btn btn-default" onClick={() => handleHistory(incident)}>
                      History
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && !error && (!isItem || accountID) && incidents.length === 0 && (
          <p className="text-center">No incidents have been reported for this {kind}.</p>
        )}
      </div>

      <Roles show={[UserRole.Admin, UserRole.Claims]}>
        {!readOnly && (
          <div className="panel-footer">
            <div className="clearfix">
              <button type="button" className="btn btn-default pull-right" onClick={() => handleNew()}>
                Report Incident
              </button>
            </div>
          </div>
        )}
      </Roles>

      {dialogIncident && resource && (
        <IncidentDialog
          key={dialogIncident.id ?? 'new'}
          incident={dialogIncident}
          resource={{
            id: resource.id,
            state: resource.state,
            accountID: isItem && 'accountID' in resource ? resource.accountID : undefined,
            duplicateOfID: isItem && 'duplicateOfID' in resource ? resource.duplicateOfID : undefined,
          }}
          onSave={handleDialogSave}
          onCancel={handleDialogClose}
        />
      )}

      {historyIncidentID && (
        <HistoryModal
          incidentID={historyIncidentID}
          timezone={historyTimezone}
          onClose={() => setHistoryIncidentID(null)}
        />
      )}

      {incidentToResolve && (!isItem || accountID) && (
        <ResolveIncidentModal
          incident={incidentToResolve}
          parentType={parentType}
          parentID={parentID}
          accountID={accountID}
          isItem={isItem}
          onClose={() => setIncidentToResolve(null)}
        />
      )}

      {lightboxImages && (
        <LightboxWithCarousel
          imageInputs={lightboxImages}
          defaultActiveIndex={lightboxIndex}
          onClose={() => setLightboxImages(null)}
        />
      )}
    </div>
  );
};
